import { Component, effect, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { RadioButtonCustomization, SelectOption } from './radiobutton-edit';

@Component({
  selector: 'lib-radiobutton-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [form]="customizationForm"
      header="Personalizzazione Radio Button"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div [formGroup]="customizationForm" class="flex flex-col gap-2">
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
            ></textarea>
          </div>
          <div class="flex w-full flex-col gap-2">
            @let optionForm = this.optionForm();
            @if (optionForm) {
              <div class="flex w-full flex-col gap-2 rounded-md">
                <p-table
                  [value]="optionForm"
                  tableStyleClass="p-fluid"
                  [scrollable]="true"
                  scrollHeight="400px"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th>Descrizione</th>
                      <th class="text-center">Valore è descrizione</th>
                      <th>Valore</th>
                      <th>Peso</th>
                      <th>Tipo</th>
                      <th class="w-0">
                        <button mat-icon-button (click)="addOption()">
                          <mat-icon svgIcon="heroicons_solid:plus"></mat-icon>
                        </button>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template
                    pTemplate="body"
                    let-option
                    let-rowIndex="rowIndex"
                  >
                    <tr>
                      <td>
                        <input
                          pInputText
                          placeholder="Descrizione"
                          [formControl]="indexOption(rowIndex).controls.label"
                        />
                      </td>
                      <td class="text-center">
                        <p-checkbox
                          [binary]="true"
                          [formControl]="
                            indexOption(rowIndex).controls
                              .valueEqualsDescription
                          "
                        ></p-checkbox>
                      </td>
                      <td>
                        <input
                          pInputText
                          placeholder="Valore"
                          [formControl]="indexOption(rowIndex).controls.value"
                        />
                      </td>
                      <td>
                        <p-input-number
                          [autofocus]="false"
                          placeholder="Peso"
                          [formControl]="indexOption(rowIndex).controls.weight"
                          mode="decimal"
                          minFractionDigits="0"
                          maxFractionDigits="2"
                        />
                      </td>
                      <td>
                        <p-select
                          [autofocus]="false"
                          [options]="optionTypes"
                          optionLabel="label"
                          optionValue="value"
                          appendTo="body"
                          [formControl]="indexOption(rowIndex).controls.type"
                        />
                      </td>
                      <td>
                        <button
                          mat-icon-button
                          (click)="removeOption(rowIndex)"
                        >
                          <mat-icon svgIcon="heroicons_solid:minus"></mat-icon>
                        </button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
              <div class="max-h-72 gap-1 overflow-y-auto">
                <label for="data">Valore predefinito</label>
                <div
                  [class]="
                    'flex w-[37rem] flex-wrap lg:w-full ' + topologyStyle
                  "
                >
                  @for (
                    option of customizationForm.controls.options.value;
                    track $index;
                    let i = $index
                  ) {
                    <div class="flex flex-row gap-2">
                      <p-radioButton
                        [autofocus]="false"
                        id="data"
                        [value]="option?.value"
                        formControlName="defaultValue"
                      ></p-radioButton
                      ><label [for]="i">
                        {{ option?.label }}
                      </label>
                    </div>
                  }
                </div>
              </div>
            }
            <div class="flex w-full flex-row">
              <label for="required" class="w-full self-start">
                Campo obbligatorio
              </label>
              <p-checkbox
                id="required"
                formControlName="required"
                [binary]="true"
                class="self-end"
              ></p-checkbox>
            </div>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    SelectModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    InputNumberModule,
    MatButtonModule,
    RadioButtonModule,
    CheckboxModule,
    MatIconModule,
    TableModule,
    TextareaModule,
  ],
})
export class RadioButtonCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<RadioButtonCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    options: this.fb.array<
      FormGroup<{
        label: FormControl<string | null>;
        value: FormControl<string | number | null>;
        valueEqualsDescription: FormControl<boolean | null>;
        type: FormControl<'string' | 'number' | null>;
        weight: FormControl<number | null>;
      }>
    >([]),
    defaultValue: [''],
    required: [false],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    // topology: [0],
  });

  optionForm = signal<SelectOption[]>([]);

  topologies = [
    {
      value: 0,
      viewValue: 'Orizzontale',
    },
    {
      value: 1,
      viewValue: 'Verticale',
    },
  ];

  optionTypes = [
    {
      label: 'string',
      value: 'string',
    },
    {
      label: 'number',
      value: 'number',
    },
  ];

  topologyStyle = 'flex-row gap-5';

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        defaultValue: widget.customization.defaultValue,
        required: widget.customization.required,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
        // topology: widget.customization.topology,
      });

      widget.customization.options.forEach((option: SelectOption) => {
        this.addOption({
          label: option.label,
          value: option.value,
          valueEqualsDescription: option.valueEqualsDescription,
          type: typeof option.value === 'number' ? 'number' : 'string',
          weight: option.weight,
        });
      });

      this.optionForm.set(widget.customization.options);
    });
  }

  addOption(config: Partial<SelectOption> = {}) {
    const newOption = this.createOptionFormGroup(config);

    this.customizationForm.controls.options.push(newOption);

    this.optionForm.set(
      this.customizationForm.controls.options.value.filter(
        (opt): opt is SelectOption =>
          opt.label != null && opt.value != null && opt.type != null,
      ),
    );
  }

  private createOptionFormGroup(config: Partial<SelectOption> = {}) {
    const newOption = this.fb.group({
      label: this.fb.control<string | null>(
        config.label ??
          `Opzione ${this.customizationForm.controls.options.length + 1}`,
      ),
      value: this.fb.control<string | number | null>(
        config.value ??
          `Opzione ${this.customizationForm.controls.options.length + 1}`,
      ),
      valueEqualsDescription: this.fb.control<boolean | null>(
        config.valueEqualsDescription ?? false,
      ),
      type: this.fb.control<'string' | 'number' | null>(
        config.type ?? 'string',
      ),
      weight: this.fb.control<number | null>(config.weight ?? 0),
    });

    // Sync value with label if valueEqualsDescription is true
    newOption.controls.label.valueChanges.subscribe((val) => {
      if (newOption.controls.valueEqualsDescription.value) {
        newOption.controls.value.setValue(val);
      }
    });

    // Adjust value type based on selected type
    newOption.controls.type.valueChanges.subscribe((val) => {
      if (val === 'string') {
        newOption.controls.value.setValue(
          newOption.controls.value.value?.toString() ?? '',
        );
      } else {
        const tmp = parseFloat(
          newOption.controls.value.value?.toString() ?? '',
        );
        if (!isNaN(tmp)) {
          newOption.controls.value.setValue(tmp);
        } else {
          newOption.controls.type.setValue('string', { emitEvent: false });
        }
      }
    });

    // Adjust type if value input changes
    newOption.controls.value.valueChanges.subscribe((val) => {
      if (!val) return;

      const tmp = parseFloat(val.toString() ?? '');
      if (isNaN(tmp)) {
        newOption.controls.type.setValue('string', { emitEvent: false });
      }
      if (val != newOption.controls.label.value) {
        newOption.controls.valueEqualsDescription.setValue(false, {
          emitEvent: false,
        });
      }
    });

    // Sync value with label when valueEqualsDescription is toggled
    newOption.controls.valueEqualsDescription.valueChanges.subscribe((val) => {
      if (val) {
        newOption.controls.value.setValue(newOption.controls.label.value, {
          emitEvent: false,
        });
      }
    });

    return newOption;
  }

  indexOption(int: number) {
    return this.customizationForm.controls.options.at(int) as FormGroup<{
      label: FormControl<string | null>;
      value: FormControl<string | null>;
      valueEqualsDescription: FormControl<boolean | null>;
      type: FormControl<'string' | 'number' | null>;
      weight: FormControl<number | null>;
    }>;
  }

  removeOption(index: number) {
    this.customizationForm.controls.options.removeAt(index);
    this.optionForm.set(
      this.customizationForm.controls.options.value.filter(
        (opt): opt is SelectOption =>
          opt.label != null && opt.value != null && opt.type != null,
      ),
    );
  }

  updateWidget(closed: boolean) {
    if (!closed) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    this.widgetService.updateWidget(id, {
      description: formValue.description,
      options: formValue.options,
      defaultValue: formValue.defaultValue,
      required: formValue.required,
      showIfRule: formValue.showIfRule || '',
      autocompleteRule: formValue.autocompleteRule || '',
      // topology: this.customizationForm.controls.topology.value!,
    });
    this.widgetService.settingVisibility.set(false);
  }
}
